import { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Modal, notification } from 'antd';

import type {
  IWorkJourneyMatrixCell,
  IWorkJourneyMatrixUser,
  IWorkJourneyReplacementCandidate,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { toExceptionUserSummary } from '@src/packages/workJourney/screens/WorkJourneyManagement/components/CoverPositionDrawers/WorkJourneyCoverAbsenceReplaceDrawer/utils/buildShiftReplacementException';
import UserRepository from '@src/repositories/UserRepository';
import ExceptionService from '@src/services/ExceptionService';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';

import type { MatrixAbsenceExcuse } from '../components/WorkJourneyMatrixAbsenceDrawer';
import type { MatrixSelectionBarAction } from '../components/WorkJourneyMatrixSelectionBar';
import {
  applyExceptionToMatrixCells,
  clearAbsenceAndLinkedReplacementsFromMatrixCells,
  clearExceptionFromMatrixCells,
  expandMatrixExceptionRangesToDates,
} from '../utils/applyOptimisticMatrixException';
import type { MatrixSelectionMeta } from '../utils/getMatrixSelectionMeta';
import { isFutureMatrixCalendarDate } from '../utils/isFutureMatrixCalendarDate';
import { isMatrixCoverOnlyUser } from '../utils/isMatrixCoverOnlyUser';
import { isReplacementCell } from '../utils/isReplacementCell';
import {
  matrixRemoveExceptionConfirmTitleId,
  matrixRemoveExceptionLabelId,
} from '../utils/isRemovableMatrixExceptionCell';
import {
  collapseSortedDatesToRanges,
  resolveMatrixExceptionDateRanges,
} from '../utils/resolveMatrixExceptionDateRanges';
import { resolveRemovableMatrixExceptionSelection } from '../utils/resolveRemovableMatrixExceptionSelection';

type UseWorkJourneyMatrixSelectionActionsParams = {
  organizationId?: string | null;
  workJourneyConfigId: string;
  selectionMeta: MatrixSelectionMeta | null;
  selectedUser: IWorkJourneyMatrixUser | null;
  cellByKey: Map<string, IWorkJourneyMatrixCell>;
  makeSelectionKey: (userId: string, date: string) => string;
  clearSelection: () => void;
  /**
   * Patch loaded matrix cells immediately and recompute day coverage
   * (`working/required`). Exception create/delete is async (triggers + ES), so
   * reloading right away often returns stale chips and totals.
   */
  patchMatrixCells: (
    patcher: (cells: IWorkJourneyMatrixCell[]) => IWorkJourneyMatrixCell[],
  ) => void;
  /** Used for replacement creates that may add a new matrix row (not only cells). */
  reloadMatrix: () => Promise<void>;
};

type AbsenceDrawerState = {
  open: boolean;
  excuse: MatrixAbsenceExcuse;
};

export const useWorkJourneyMatrixSelectionActions = ({
  organizationId,
  workJourneyConfigId,
  selectionMeta,
  selectedUser,
  cellByKey,
  makeSelectionKey,
  clearSelection,
  patchMatrixCells,
  reloadMatrix,
}: UseWorkJourneyMatrixSelectionActionsParams) => {
  const intl = useIntl();
  const exceptionServiceRef = useRef(new ExceptionService());
  const userRepositoryRef = useRef(new UserRepository());
  const managementServiceRef = useRef(new WorkJourneyManagementService());

  const orgIdFromStore = useSelector(
    ({ organizations }: { organizations: { organization: { id?: string } } }) =>
      organizations.organization?.id,
  );
  const resolvedOrgId = organizationId || orgIdFromStore;

  const [absenceDrawer, setAbsenceDrawer] = useState<AbsenceDrawerState>({
    open: false,
    excuse: 'justified_absence',
  });
  const [vacationOpen, setVacationOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const infoDate = selectionMeta?.startDate ?? '';
  const infoCell = useMemo(() => {
    if (!selectionMeta) return null;
    return cellByKey.get(makeSelectionKey(selectionMeta.userId, infoDate)) ?? null;
  }, [cellByKey, infoDate, makeSelectionKey, selectionMeta]);

  const removableExceptionSelection = useMemo(() => {
    if (!selectionMeta) return null;
    const cells = selectionMeta.dates.map(
      date => cellByKey.get(makeSelectionKey(selectionMeta.userId, date)) ?? null,
    );
    return resolveRemovableMatrixExceptionSelection(cells);
  }, [cellByKey, makeSelectionKey, selectionMeta]);

  const removableExceptionCell = removableExceptionSelection?.cell ?? null;
  const removeExceptionCopyKey = removableExceptionSelection?.copyKey ?? null;

  const canRemoveException = Boolean(removeExceptionCopyKey);
  const isCoverOnlySelection = isMatrixCoverOnlyUser(selectedUser);

  const formatExceptionRangeLabel = useCallback(
    (startDate: string, endDate: string): string => {
      const locale = intl.locale === 'es' ? 'es-CL' : 'en-US';
      const start = new Date(`${startDate}T12:00:00`);
      const end = new Date(`${endDate}T12:00:00`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return startDate === endDate ? startDate : `${startDate} – ${endDate}`;
      }
      const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
      if (startDate === endDate) {
        return new Intl.DateTimeFormat(locale, opts).format(start);
      }
      return `${new Intl.DateTimeFormat(locale, opts).format(start)} – ${new Intl.DateTimeFormat(
        locale,
        opts,
      ).format(end)}`;
    },
    [intl.locale],
  );

  const closeAllDrawers = useCallback(() => {
    setAbsenceDrawer(prev => ({ ...prev, open: false }));
    setVacationOpen(false);
    setReplaceOpen(false);
    setInfoOpen(false);
  }, []);

  const handleUndoException = useCallback(
    async (exceptionId: string, sourceCell?: IWorkJourneyMatrixCell | null) => {
      if (!resolvedOrgId || !exceptionId) return;
      setSubmitting(true);
      try {
        await exceptionServiceRef.current.deleteException(exceptionId, resolvedOrgId);
        patchMatrixCells(cells => {
          if (sourceCell && !isReplacementCell(sourceCell)) {
            const startDate = sourceCell.exceptionStartDate || sourceCell.date;
            const endDate =
              sourceCell.exceptionEndDate || sourceCell.exceptionStartDate || sourceCell.date;
            return clearAbsenceAndLinkedReplacementsFromMatrixCells(cells, {
              absenceExceptionId: exceptionId,
              absentUserId: sourceCell.userId,
              startDate,
              endDate,
            });
          }
          return clearExceptionFromMatrixCells(cells, exceptionId);
        });
        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        setInfoOpen(false);
        clearSelection();
      } catch (error) {
        console.error('Error deleting exception from matrix info:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [clearSelection, intl, patchMatrixCells, resolvedOrgId],
  );

  const confirmRemoveException = useCallback(() => {
    const cell = removableExceptionCell;
    const exceptionId = cell?.exceptionId;
    const copyKey = removeExceptionCopyKey;
    if (!exceptionId || !cell || !copyKey) return;

    const rangeStart = cell.exceptionStartDate || cell.date;
    const rangeEnd = cell.exceptionEndDate || cell.exceptionStartDate || cell.date;
    const rangeLabel = formatExceptionRangeLabel(rangeStart, rangeEnd);
    const labelId = matrixRemoveExceptionLabelId(copyKey);
    const titleId = matrixRemoveExceptionConfirmTitleId(copyKey);

    Modal.confirm({
      centered: true,
      okType: 'danger',
      title: intl.formatMessage({ id: titleId }),
      content: intl.formatMessage(
        { id: 'workJourney.management.matrix.selectionBar.removeConfirmContent' },
        { range: rangeLabel },
      ),
      okText: intl.formatMessage({ id: labelId }),
      cancelText: intl.formatMessage({ id: 'button.cancel' }),
      onOk: () => handleUndoException(exceptionId, cell),
    });
  }, [
    formatExceptionRangeLabel,
    handleUndoException,
    intl,
    removableExceptionCell,
    removeExceptionCopyKey,
  ]);

  const handleBarAction = useCallback(
    (action: MatrixSelectionBarAction) => {
      if (!selectionMeta || !selectedUser) return;

      // Cover-only rows cannot create absences / vacation / new replacements.
      if (
        isCoverOnlySelection &&
        (action === 'justified_absence' ||
          action === 'unjustified_absence' ||
          action === 'vacation' ||
          action === 'replace')
      ) {
        return;
      }

      if (action === 'justified_absence') {
        setAbsenceDrawer({ open: true, excuse: 'justified_absence' });
        return;
      }
      if (action === 'unjustified_absence') {
        setAbsenceDrawer({ open: true, excuse: 'unjustified_absence' });
        return;
      }
      if (action === 'vacation') {
        setVacationOpen(true);
        return;
      }
      if (action === 'replace') {
        setReplaceOpen(true);
        return;
      }
      if (action === 'remove_exception') {
        confirmRemoveException();
        return;
      }
      if (action === 'info') {
        if (selectionMeta.count !== 1) return;
        setInfoOpen(true);
      }
    },
    [confirmRemoveException, isCoverOnlySelection, selectedUser, selectionMeta],
  );

  const resolveUserSummary = useCallback(
    async (userId: string, fallback: IWorkJourneyMatrixUser | IWorkJourneyReplacementCandidate) => {
      if (!resolvedOrgId) return toExceptionUserSummary(fallback);
      try {
        const doc = await userRepositoryRef.current.getUser(resolvedOrgId, userId);
        return toExceptionUserSummary(doc ?? fallback) ?? toExceptionUserSummary(fallback);
      } catch {
        return toExceptionUserSummary(fallback);
      }
    },
    [resolvedOrgId],
  );

  const handleAbsenceSubmit = useCallback(
    async (payload: {
      excuse: MatrixAbsenceExcuse;
      reasonType: string | null;
      startDate: string;
      endDate: string;
      description: string;
    }) => {
      if (!selectedUser || !resolvedOrgId || !selectionMeta) return;
      setSubmitting(true);
      try {
        const userSummary = await resolveUserSummary(selectedUser.id, selectedUser);
        if (!userSummary) throw new Error('Missing user summary');

        const type =
          payload.excuse === 'unjustified_absence'
            ? 'unjustified_absence'
            : payload.reasonType || 'justified_absence';

        const ranges = resolveMatrixExceptionDateRanges({
          formStartDate: payload.startDate,
          formEndDate: payload.endDate,
          selectionMeta,
        });
        if (ranges.length === 0) return;

        const created = await Promise.all(
          ranges.map(range =>
            exceptionServiceRef.current.createException(
              {
                type,
                isExcused: payload.excuse === 'justified_absence',
                startDate: range.startDate,
                endDate: range.endDate,
                user: userSummary,
                description: payload.description || undefined,
              },
              resolvedOrgId,
            ),
          ),
        );

        patchMatrixCells(cells =>
          ranges.reduce((next, range, index) => {
            const exceptionId = created[index]?.id;
            if (!exceptionId) return next;
            return applyExceptionToMatrixCells(next, {
              userId: selectedUser.id,
              dates: expandMatrixExceptionRangesToDates([range]),
              exceptionType: type,
              description: payload.description || undefined,
              exceptionId,
              exceptionStartDate: range.startDate,
              exceptionEndDate: range.endDate,
            });
          }, cells),
        );

        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        setAbsenceDrawer(prev => ({ ...prev, open: false }));
        clearSelection();
      } catch (error) {
        console.error('Error creating matrix absence:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      clearSelection,
      intl,
      patchMatrixCells,
      resolveUserSummary,
      resolvedOrgId,
      selectedUser,
      selectionMeta,
    ],
  );

  const handleVacationSubmit = useCallback(
    async (payload: { startDate: string; endDate: string; description: string }) => {
      if (!selectedUser || !resolvedOrgId || !selectionMeta) return;
      setSubmitting(true);
      try {
        const userSummary = await resolveUserSummary(selectedUser.id, selectedUser);
        if (!userSummary) throw new Error('Missing user summary');

        const ranges = resolveMatrixExceptionDateRanges({
          formStartDate: payload.startDate,
          formEndDate: payload.endDate,
          selectionMeta,
        });
        if (ranges.length === 0) return;

        const created = await Promise.all(
          ranges.map(range =>
            exceptionServiceRef.current.createException(
              {
                type: 'vacation',
                isExcused: true,
                startDate: range.startDate,
                endDate: range.endDate,
                user: userSummary,
                description: payload.description || undefined,
              },
              resolvedOrgId,
            ),
          ),
        );

        patchMatrixCells(cells =>
          ranges.reduce((next, range, index) => {
            const exceptionId = created[index]?.id;
            if (!exceptionId) return next;
            return applyExceptionToMatrixCells(next, {
              userId: selectedUser.id,
              dates: expandMatrixExceptionRangesToDates([range]),
              exceptionType: 'vacation',
              description: payload.description || undefined,
              exceptionId,
              exceptionStartDate: range.startDate,
              exceptionEndDate: range.endDate,
            });
          }, cells),
        );

        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        setVacationOpen(false);
        clearSelection();
      } catch (error) {
        console.error('Error creating matrix vacation:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      clearSelection,
      intl,
      patchMatrixCells,
      resolveUserSummary,
      resolvedOrgId,
      selectedUser,
      selectionMeta,
    ],
  );

  const handleReplaceContinue = useCallback(
    async (candidate: IWorkJourneyReplacementCandidate) => {
      if (!selectedUser || !resolvedOrgId || !selectionMeta) return;
      setSubmitting(true);
      try {
        const [substituteSummary, replacedSummary, coverConfig] = await Promise.all([
          resolveUserSummary(candidate.id, candidate),
          resolveUserSummary(selectedUser.id, selectedUser),
          managementServiceRef.current.getWorkJourneyConfigSnapshot(
            resolvedOrgId,
            workJourneyConfigId,
          ),
        ]);

        if (!substituteSummary || !replacedSummary) {
          throw new Error('Missing user summaries for shift replacement');
        }

        const ranges = collapseSortedDatesToRanges(selectionMeta.dates);
        await Promise.all(
          ranges.map(range =>
            exceptionServiceRef.current.createShiftReplacementException(
              {
                user: substituteSummary,
                replacedUser: replacedSummary,
                startDate: range.startDate,
                endDate: range.endDate,
                replacementWorkJourneyConfigId: workJourneyConfigId,
                replacementWorkShiftId: coverConfig?.workShift?.id,
              },
              resolvedOrgId,
            ),
          ),
        );

        // Belt-and-suspenders for past/today cover days: the onCreate trigger
        // creates coverage ARs asynchronously. Ensure markable days already have
        // a real attendance record before the matrix reload paints REM chips.
        const pastOrTodayDates = selectionMeta.dates.filter(
          date => !isFutureMatrixCalendarDate(date),
        );
        if (pastOrTodayDates.length > 0) {
          await Promise.all(
            pastOrTodayDates.map(date =>
              managementServiceRef.current
                .createMatrixAttendance({
                  organizationId: resolvedOrgId,
                  workJourneyConfigId,
                  userId: candidate.id,
                  date,
                })
                .catch(error => {
                  console.error('Error ensuring coverage attendance after replace:', error);
                }),
            ),
          );
        }

        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        setReplaceOpen(false);
        clearSelection();
        await reloadMatrix();
      } catch (error) {
        console.error('Error creating matrix replacement:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      clearSelection,
      intl,
      reloadMatrix,
      resolveUserSummary,
      resolvedOrgId,
      selectedUser,
      selectionMeta,
      workJourneyConfigId,
    ],
  );

  return {
    absenceDrawer,
    vacationOpen,
    replaceOpen,
    infoOpen,
    infoDate,
    infoCell,
    canRemoveException,
    removeExceptionCopyKey,
    /** Cover-only / reemplazante row — selection bar hides create actions. */
    isCoverOnlySelection,
    submitting,
    handleBarAction,
    closeAbsenceDrawer: () => setAbsenceDrawer(prev => ({ ...prev, open: false })),
    closeVacationDrawer: () => setVacationOpen(false),
    closeReplaceDrawer: () => setReplaceOpen(false),
    closeInfoDrawer: () => setInfoOpen(false),
    closeAllDrawers,
    handleAbsenceSubmit,
    handleVacationSubmit,
    handleReplaceContinue,
    handleUndoException,
  };
};
